// @ts-check
// Executa os comandos do registro (client/src/lib/command-registry.js) e monta o contexto a partir do que está aberto:
// lista e view, seleção da tabela ou do kanban, registro aberto, favoritos e o papel do membro. O registro de comandos
// só oferece o que o papel permite; aqui cada ação chama a mesma função da tela equivalente, e a API confere de novo.
import { navigate, routeState } from '../../app/router.js';
import {
  favoritesState,
  getViewsForObject,
  hasCurrentPermissionFlag,
  isCoordinationFocusedState,
  objectsByNameSingular,
  replaceViewLocally,
} from '../../data/metadata-store.js';
import { createRecord, deleteRecordOptimistically } from '../../data/record-store.js';
import { restoreOneRecord, updateView } from '../../data/rest.js';
import { addFavorite, findDuplicateRecords, MAXIMUM_MERGE_RECORDS, removeFavorite } from '../../data/parity.js';
import { downloadRecordsCsv } from '../../data/dashboards.js';
import { downloadBlob, toFileName } from '../../lib/csv.js';
import { getRecordLabel } from '../../lib/field-metadata.js';
import { canReadObjectRecords, canUpdateObjectRecords } from '../../lib/permissions.js';
import { buildContextualCommands, buildListCommands, buildSelectionCommands } from '../../lib/command-registry.js';
import { togglePinnedCommandKey } from '../../../../shared/command-menu/pinned-commands.js';
import { enqueueSnackbar, showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';
import {
  closeSidePanel,
  openCommandMenu,
  openCreateRecordPanel,
  openMergeRecordsPanel,
  openNewConversationPanel,
  openNoteEditorPanel,
  openUpdateRecordsPanel,
} from '../side-panel/side-panel-state.js';
import { activeRecordIndexState, getViewQuery, isDeletedFilterActive, setDeletedFilterActive } from '../views/view-state.js';
import { exportViewRecords } from '../views/export-records-button.js';
import { findOwnerField, findStageField } from '../object-record/record-selection-actions.js';
import { canClassifyAsNotLead, classifyRecordAsNotLead, classifyRecordsAsNotLead, supportsNotLead } from '../object-record/not-lead.js';
import { runTrashAction } from '../object-record/trash-actions.js';
import { getActivityTargetConfig } from '../record-show/activity-targets.js';
import { deleteRecordSelection, exportRecordSelection, favoriteRecordSelection, getVisibleFieldNames } from './selection-actions.js';
import { startCampaignFromList } from '../email/campaign-audience.js';
// comandos da 2.0.0 (LGPD, campanhas de WhatsApp e fluxos): a área dona executa (spec-2.0.0 seção 5.3)
import { campaignCommandContext, campaignRecordCommandContext, isFlowDrilldownView, runCampaignCommand } from '../campaigns/campaign-commands.js';
import {
  activeConversationState,
  activeRecordShowState,
  activeSelectionTargetState,
  requestCommandConfirmation,
  requestListCommand,
} from './command-state.js';

/**
 * @typedef {import('../../lib/command-registry.js').CommandContext} CommandContext
 * @typedef {import('../../lib/command-registry.js').CommandDescriptor} CommandDescriptor
 * @typedef {import('../../lib/field-metadata.js').ObjectMetadata} ObjectMetadata
 * @typedef {import('../../data/metadata-store.js').View} View
 * @typedef {import('./command-state.js').SelectionTarget} SelectionTarget
 * @typedef {import('./command-state.js').RecordShowTarget} RecordShowTarget
 * @typedef {{ list?: { objectMetadata: ObjectMetadata, view: View } | null, selection?: SelectionTarget | null, record?: RecordShowTarget | null,
 *   conversation?: import('./command-state.js').ConversationTarget | null }} CommandRuntime
 */

/** @param {ObjectMetadata} objectMetadata */
function toCommandObject(objectMetadata) {
  return {
    nameSingular: objectMetadata.nameSingular,
    namePlural: objectMetadata.namePlural,
    labelSingular: translateMetadataLabel(objectMetadata.labelSingular),
    labelPlural: translateMetadataLabel(objectMetadata.labelPlural),
    isSystem: Boolean(objectMetadata.isSystem),
    duplicateCriteria: objectMetadata.duplicateCriteria ?? null,
    permissions: objectMetadata.permissions,
  };
}

/** Pode criar tarefa ou nota vinculada: lê e edita o objeto da atividade e a junção existe. @param {'task' | 'note'} activity @param {string} target */
function canCreateActivity(activity, target) {
  const activityObject = objectsByNameSingular.value.get(activity);
  return Boolean(activityObject && canUpdateObjectRecords(activityObject) && canReadObjectRecords(activityObject) && getActivityTargetConfig(activity, target));
}

/** @param {SelectionTarget} target */
export function toCommandSelection(target) {
  const stageField = findStageField(target.objectMetadata, target.view);
  return {
    count: target.count,
    isTrashMode: target.isTrashMode,
    hasOwnerField: Boolean(findOwnerField(target.objectMetadata)),
    hasStageField: Boolean(stageField),
    stageFieldLabel: stageField ? translateMetadataLabel(stageField.label) : '',
    supportsNotLead: supportsNotLead(target.objectMetadata),
    isFlowDrilldown: isFlowDrilldownView(target.view),
  };
}

/** Base do contexto: o papel do membro e os comandos da 2.0.0 que as áreas já entregaram. */
function baseContext() {
  return {
    hasPermissionFlag: hasCurrentPermissionFlag,
    isCoordinationFocused: isCoordinationFocusedState.value,
    ...campaignCommandContext(),
  };
}

/**
 * Contexto do que está aberto agora (menu de comandos).
 * @returns {{ context: CommandContext, runtime: CommandRuntime }}
 */
export function buildCurrentCommandContext() {
  const route = routeState.value;
  const list = route.name === 'recordIndex' ? activeRecordIndexState.value : null;
  const selection = activeSelectionTargetState.value;
  const recordShow = route.name === 'recordShow' ? activeRecordShowState.value : null;
  if (selection && selection.count > 0) {
    return {
      context: { ...baseContext(), page: 'recordIndex', object: toCommandObject(selection.objectMetadata), view: selection.view, selection: toCommandSelection(selection) },
      runtime: { selection, list },
    };
  }
  if (recordShow?.record) {
    const { objectMetadata, record } = recordShow;
    return {
      context: {
        ...baseContext(),
        page: 'recordShow',
        object: toCommandObject(objectMetadata),
        record: {
          id: record.id,
          isDeleted: Boolean(record.deletedAt),
          isFavorite: favoritesState.value.some((item) => item.type === 'RECORD' && item.targetRecordId === record.id),
          canClassifyNotLead: canClassifyAsNotLead(objectMetadata, record),
          ...campaignRecordCommandContext(objectMetadata, record),
        },
        canCreateTask: canCreateActivity('task', objectMetadata.nameSingular),
        canCreateNote: canCreateActivity('note', objectMetadata.nameSingular),
      },
      runtime: { record: recordShow },
    };
  }
  if (list) {
    return {
      context: { ...baseContext(), ...buildListContext(list.objectMetadata, list.view) },
      runtime: { list },
    };
  }
  const conversation = activeConversationState.value;
  if (conversation) {
    return {
      context: {
        ...baseContext(), page: 'other',
        conversation: { threadId: conversation.threadId, runningBotName: conversation.runningBotName, startableBots: conversation.startableBots, isServiceWindowOpen: conversation.isServiceWindowOpen },
      },
      runtime: { conversation },
    };
  }
  return { context: { ...baseContext(), page: 'other' }, runtime: {} };
}

/** @param {ObjectMetadata} objectMetadata @param {View} view */
function buildListContext(objectMetadata, view) {
  return {
    page: /** @type {const} */ ('recordIndex'),
    object: toCommandObject(objectMetadata),
    view,
    viewTypes: [...new Set(getViewsForObject(objectMetadata.id).filter((candidate) => candidate.id !== view.id).map((candidate) => candidate.type))],
    isTrashMode: isDeletedFilterActive(view),
  };
}

/** Comandos do menu de comandos para o que está aberto. */
export function getCurrentCommands() {
  const { context, runtime } = buildCurrentCommandContext();
  return { commands: buildContextualCommands(context), runtime };
}

/** Comandos do clique direito: só os da seleção. @param {SelectionTarget} target */
export function getSelectionCommands(target) {
  return buildSelectionCommands({ ...baseContext(), page: 'recordIndex', object: toCommandObject(target.objectMetadata), view: target.view, selection: toCommandSelection(target) });
}

/** Comandos da lista para os botões fixados. @param {ObjectMetadata} objectMetadata @param {View} view */
export function getListCommands(objectMetadata, view) {
  return buildListCommands({ ...baseContext(), ...buildListContext(objectMetadata, view) });
}

/**
 * Etapas para escolher quando a seleção muda de etapa um card por vez (kanban); sem elas, o painel de atualização.
 * @param {SelectionTarget | null | undefined} target
 */
export const getStageChoices = (target) => (target?.onMoveToStage && target.stageOptions ? target.stageOptions : null);

/**
 * Rótulo de um comando, já traduzido.
 * @param {CommandDescriptor} command
 */
export function getCommandLabel(command) {
  const params = { ...(command.labelParams ?? {}) };
  if (typeof params.pageKey === 'string') params.page = t(params.pageKey);
  return t(command.labelKey, params);
}

/** @param {() => Promise<unknown> | unknown} action */
async function guarded(action) {
  try {
    await action();
  } catch (error) {
    showErrorSnackbar(error);
  }
}

/**
 * Executa um comando. `stageValue`: etapa escolhida quando o comando é "Mudar etapa" com escolha (kanban).
 * @param {CommandDescriptor} command @param {CommandRuntime} runtime @param {{ stageValue?: string | null }} [options]
 */
export async function runCommand(command, runtime, options = {}) {
  if (command.href) {
    closeSidePanel();
    navigate(command.href);
    return;
  }
  let isCampaignCommand = false;
  await guarded(async () => {
    isCampaignCommand = await runCampaignCommand(command, runtime);
  });
  if (isCampaignCommand) return;
  const [scope] = command.id.split('.');
  if (scope === 'selection' && runtime.selection) await guarded(() => runSelectionCommand(command, /** @type {SelectionTarget} */ (runtime.selection), options));
  else if (scope === 'record' && runtime.record) await guarded(() => runRecordCommand(command, /** @type {RecordShowTarget} */ (runtime.record)));
  else if (scope === 'list' && runtime.list) await guarded(() => runListCommand(command, /** @type {{ objectMetadata: ObjectMetadata, view: View }} */ (runtime.list)));
}

/**
 * @param {CommandDescriptor} command @param {SelectionTarget} target @param {{ stageValue?: string | null }} options
 */
async function runSelectionCommand(command, target, options) {
  const { objectMetadata, view } = target;
  const ids = () => target.resolveSelectedIds();
  switch (command.id) {
    case 'selection.open': {
      const [id] = await ids();
      closeSidePanel();
      if (id) navigate(`/object/${objectMetadata.nameSingular}/${id}`);
      return;
    }
    case 'selection.update':
      openUpdateRecordsPanel({ objectNameSingular: objectMetadata.nameSingular, ids: await ids(), presetFieldName: null, onDone: target.onChanged });
      return;
    case 'selection.assign':
      openUpdateRecordsPanel({ objectNameSingular: objectMetadata.nameSingular, ids: await ids(), presetFieldName: findOwnerField(objectMetadata)?.name ?? null, onDone: target.onChanged });
      return;
    case 'selection.changeStage':
      if (options.stageValue !== undefined && target.onMoveToStage) {
        closeSidePanel();
        await target.onMoveToStage(options.stageValue);
        return;
      }
      openUpdateRecordsPanel({ objectNameSingular: objectMetadata.nameSingular, ids: await ids(), presetFieldName: findStageField(objectMetadata, view)?.name ?? null, onDone: target.onChanged });
      return;
    case 'selection.notLead': {
      closeSidePanel();
      if (await classifyRecordsAsNotLead(objectMetadata, await ids())) target.onChanged();
      return;
    }
    case 'selection.merge':
      openMergeRecordsPanel({ objectNameSingular: objectMetadata.nameSingular, ids: await ids(), onDone: target.onChanged });
      return;
    case 'selection.export':
      closeSidePanel();
      await exportRecordSelection({ objectMetadata, view, ids: await ids(), isAllMatchingSelected: target.isAllMatchingSelected });
      return;
    case 'selection.emailCampaign':
      closeSidePanel();
      // na lista de um clique num gráfico, "todos" vira a lista de ids: o recorte do painel não cabe no filtro da campanha
      await startCampaignFromList({ objectMetadata, view, ids: target.isAllMatchingSelected && !getViewQuery(view).drilldown ? null : await ids() });
      return;
    case 'selection.favorite':
      closeSidePanel();
      await favoriteRecordSelection({ objectMetadata, ids: await ids() });
      return;
    case 'selection.delete':
      closeSidePanel();
      requestCommandConfirmation({
        title: t('parity.bulk.confirmDeleteTitle', { count: target.count }),
        subtitle: t('parity.bulk.confirmDeleteSubtitle'),
        confirmLabel: t('parity.bulk.delete'),
        accent: 'danger',
        onConfirm: async () => deleteRecordSelection({ objectMetadata, ids: await ids(), onDeleted: target.onDeleted, onChanged: target.onChanged }),
      });
      return;
    case 'selection.restore':
    case 'selection.destroy': {
      closeSidePanel();
      const action = command.id === 'selection.restore' ? 'restore' : 'destroy';
      requestCommandConfirmation({
        title: t(action === 'destroy' ? 'trash.confirmDestroyTitle' : 'trash.confirmRestoreTitle', { count: target.count }),
        subtitle: action === 'destroy' ? t('trash.confirmDestroySubtitle', { count: target.count }) : t('trash.confirmRestoreSubtitle'),
        confirmLabel: t(action === 'destroy' ? 'trash.destroy' : 'trash.restore'),
        accent: action === 'destroy' ? 'danger' : 'blue',
        onConfirm: async () => {
          const doneIds = await runTrashAction(objectMetadata, action, await ids());
          target.onDeleted(doneIds);
        },
      });
      return;
    }
    default:
  }
}

/**
 * @param {CommandDescriptor} command @param {RecordShowTarget} target
 */
async function runRecordCommand(command, target) {
  const { objectMetadata, record } = target;
  const recordLabel = getRecordLabel(objectMetadata, record) || t('record.untitled');
  switch (command.id) {
    case 'record.favorite':
      closeSidePanel();
      await addFavorite({ type: 'RECORD', objectNameSingular: objectMetadata.nameSingular, recordId: record.id });
      showSuccessSnackbar(t('commands.record.favorited', { name: recordLabel }));
      return;
    case 'record.unfavorite': {
      closeSidePanel();
      const favorite = favoritesState.value.find((item) => item.type === 'RECORD' && item.targetRecordId === record.id);
      if (favorite) await removeFavorite(favorite.id);
      return;
    }
    case 'record.createTask': {
      const config = getActivityTargetConfig('task', objectMetadata.nameSingular);
      if (!config) return;
      openCreateRecordPanel({
        objectNameSingular: 'task',
        onCreated: async (task) => {
          closeSidePanel();
          try {
            await createRecord(config.junctionObject, { [config.activityIdFieldName]: task.id, [config.targetIdFieldName]: record.id });
            showSuccessSnackbar(t('commands.record.taskLinked', { name: recordLabel }));
            target.reload();
          } catch (error) {
            showErrorSnackbar(error);
          }
        },
      });
      return;
    }
    case 'record.createNote':
      openNoteEditorPanel({ noteId: null, targetObjectNameSingular: objectMetadata.nameSingular, targetRecordId: record.id, onSaved: () => target.reload() });
      return;
    case 'record.newConversation': {
      const isPerson = objectMetadata.nameSingular === 'person';
      const email = isPerson ? record.emails?.primaryEmail ?? '' : '';
      const phone = isPerson && record.phones?.primaryPhoneNumber ? `${record.phones.primaryPhoneCallingCode ?? '+55'}${record.phones.primaryPhoneNumber}` : '';
      openNewConversationPanel({
        defaultChannelType: email || !phone ? 'EMAIL' : 'WHATSAPP',
        defaultTo: { EMAIL: email, WHATSAPP: phone },
        personId: isPerson ? record.id : record.pointOfContactId ?? null,
        onCreated: (thread) => navigate(`/conversations/${thread.id}`),
      });
      return;
    }
    case 'record.merge': {
      const duplicates = await findDuplicateRecords(objectMetadata, record.id);
      if (duplicates.length === 0) {
        closeSidePanel();
        enqueueSnackbar({ message: t('commands.record.noDuplicates'), variant: 'default' });
        return;
      }
      openMergeRecordsPanel({
        objectNameSingular: objectMetadata.nameSingular,
        ids: [record.id, ...duplicates.map((duplicate) => duplicate.id)].slice(0, MAXIMUM_MERGE_RECORDS),
        onDone: (mergedRecordId) => (mergedRecordId === record.id ? target.reload() : navigate(`/object/${objectMetadata.nameSingular}/${mergedRecordId}`)),
      });
      return;
    }
    case 'record.export': {
      closeSidePanel();
      const indexView = getViewsForObject(objectMetadata.id).find((view) => view.key === 'INDEX');
      const { blob, fileName } = await downloadRecordsCsv(objectMetadata.namePlural, {
        filter: { field: 'id', operator: 'eq', value: record.id }, orderBy: [], fields: indexView ? getVisibleFieldNames(indexView) : undefined, viewId: indexView?.id,
      });
      downloadBlob(blob, fileName ?? toFileName(recordLabel));
      showSuccessSnackbar(t('export.done'));
      return;
    }
    case 'record.notLead':
      closeSidePanel();
      await classifyRecordAsNotLead(objectMetadata, record);
      return;
    case 'record.delete':
      closeSidePanel();
      await deleteRecordOptimistically(objectMetadata, record.id);
      showSuccessSnackbar(t('recordShow.deleted'));
      navigate(`/objects/${objectMetadata.namePlural}`);
      return;
    case 'record.restore':
      closeSidePanel();
      if (target.restore) await target.restore();
      else {
        await restoreOneRecord(objectMetadata, record.id);
        showSuccessSnackbar(t('recordShow.restoredMessage'));
        target.reload();
      }
      return;
    case 'record.destroy':
      closeSidePanel();
      requestCommandConfirmation({
        title: t('trash.confirmDestroyTitle', { count: 1 }),
        subtitle: t('trash.confirmDestroySubtitle', { count: 1 }),
        confirmLabel: t('trash.destroy'),
        accent: 'danger',
        onConfirm: async () => {
          const doneIds = await runTrashAction(objectMetadata, 'destroy', [record.id]);
          if (doneIds.includes(record.id)) navigate(`/objects/${objectMetadata.namePlural}`);
        },
      });
      return;
    default:
  }
}

/**
 * @param {CommandDescriptor} command @param {{ objectMetadata: ObjectMetadata, view: View }} target
 */
async function runListCommand(command, { objectMetadata, view }) {
  closeSidePanel();
  if (command.id.startsWith('list.layout.')) {
    const type = command.id.slice('list.layout.'.length);
    const existing = getViewsForObject(objectMetadata.id).find((candidate) => candidate.type === type && candidate.id !== view.id);
    if (existing) navigate(`/objects/${objectMetadata.namePlural}?viewId=${existing.id}`);
    else requestListCommand('createView', view.id, { viewType: type });
    return;
  }
  switch (command.id) {
    case 'list.import':
      requestListCommand('import', view.id);
      return;
    case 'list.export':
      await exportViewRecords(objectMetadata, view);
      return;
    case 'list.emailCampaign':
      await startCampaignFromList({ objectMetadata, view });
      return;
    case 'list.seeDeleted':
      setDeletedFilterActive(view, true);
      return;
    case 'list.hideDeleted':
      setDeletedFilterActive(view, false);
      return;
    case 'list.createView':
      requestListCommand('createView', view.id);
      return;
    case 'list.filter':
      requestListCommand('filter', view.id);
      return;
    case 'list.sort':
      requestListCommand('sort', view.id);
      return;
    default:
  }
}

/**
 * Fixa ou desafixa um comando da lista na view (até dois); a view principal sintética não grava.
 * @param {View} view @param {string} pinnableKey
 */
export async function togglePinnedListCommand(view, pinnableKey) {
  const pinnedCommandKeys = togglePinnedCommandKey(view.pinnedCommandKeys ?? [], pinnableKey);
  replaceViewLocally({ ...view, pinnedCommandKeys });
  try {
    await updateView(view.id, { pinnedCommandKeys });
  } catch (error) {
    replaceViewLocally(view);
    showErrorSnackbar(error);
  }
}

/** Abre o menu de comandos a partir do clique direito ("Mais ações"). */
export function openMoreActions() {
  openCommandMenu();
}
